import { useMemo, useState } from 'react';
import { subDays } from 'date-fns';
import { Button } from '@/components/ui/button';

/**
 * Small preset date-range control shared by the user Engagement/Earnings sections and the per-post
 * content engagement chart. CMP's statistics module has a full `DateRangeDropDownView`
 * (Today/7d/30d/90d/All time + custom range); this is a deliberately smaller port — fixed presets
 * ending "now" — sufficient for an admin overview.
 */
export type RangeDays = 7 | 30 | 90 | 365;

// Default presets preserve the original user Engagement/Earnings controls (7/30/90). Callers that
// want a wider window (e.g. per-post content engagement, to surface older activity) pass their own.
const PRESETS: { label: string; days: RangeDays }[] = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
];

export const CONTENT_PRESETS: { label: string; days: RangeDays }[] = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '1Y', days: 365 },
];

export function useRangePreset(defaultDays: RangeDays = 30) {
  const [days, setDays] = useState<RangeDays>(defaultDays);
  return useMemo(() => {
    const end = new Date();
    const start = subDays(end, days);
    return { days, setDays, startISO: start.toISOString(), endISO: end.toISOString() };
  }, [days]);
}

export function RangePresetPicker({
  days,
  onChange,
  presets = PRESETS,
}: {
  days: RangeDays;
  onChange: (days: RangeDays) => void;
  presets?: { label: string; days: RangeDays }[];
}) {
  return (
    <div className="flex gap-1">
      {presets.map((p) => (
        <Button
          key={p.days}
          type="button"
          size="sm"
          variant={p.days === days ? 'default' : 'outline'}
          onClick={() => onChange(p.days)}
        >
          {p.label}
        </Button>
      ))}
    </div>
  );
}
